import { useState, useEffect } from 'react';
import { EnhancedAssumptions, PresetType } from '../types';
import { INITIAL_ASSUMPTIONS, SCENARIO_OPTIONS } from '../constants';

export const useAssumptions = () => {
  const [assumptions, setAssumptions] = useState<EnhancedAssumptions>(INITIAL_ASSUMPTIONS);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('enhanced-house-decision-assumptions');
    if (saved) {
      setAssumptions(JSON.parse(saved));
    }
  }, []);

  // Save to localStorage when assumptions change
  useEffect(() => {
    localStorage.setItem('enhanced-house-decision-assumptions', JSON.stringify(assumptions));
  }, [assumptions]);

  const updateAssumption = (key: keyof EnhancedAssumptions, value: any) => {
    setAssumptions(prev => ({ ...prev, [key]: value }));
  };

  const handleNumberInput = (key: keyof EnhancedAssumptions, inputValue: string) => {
    // Allow empty string to clear the input
    if (inputValue === '') {
      setAssumptions(prev => ({ ...prev, [key]: 0 }));
      return;
    }

    // Parse the number, handling both integers and decimals
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      setAssumptions(prev => ({ ...prev, [key]: numValue }));
    }
  };

  const updateScenario = (type: string, scenarioKey: string) => {
    if (type === 'propertyValue') {
      const scenario = SCENARIO_OPTIONS.propertyValue[scenarioKey as keyof typeof SCENARIO_OPTIONS.propertyValue];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          propertyValueScenario: scenarioKey,
          propertyValue: scenario.value
        }));
      }
    } else if (type === 'monthlyRent') {
      const scenario = SCENARIO_OPTIONS.monthlyRent[scenarioKey as keyof typeof SCENARIO_OPTIONS.monthlyRent];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          monthlyRentScenario: scenarioKey,
          monthlyRent: scenario.value
        }));
      }
    } else if (type === 'propertyAppreciation') {
      const scenario = SCENARIO_OPTIONS.propertyAppreciation[scenarioKey as keyof typeof SCENARIO_OPTIONS.propertyAppreciation];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          propertyAppreciationScenario: scenarioKey,
          propertyAppreciation: scenario.value
        }));
      }
    } else if (type === 'rentGrowth') {
      const scenario = SCENARIO_OPTIONS.rentGrowth[scenarioKey as keyof typeof SCENARIO_OPTIONS.rentGrowth];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          rentGrowthScenario: scenarioKey,
          rentGrowth: scenario.value
        }));
      }
    } else if (type === 'stockReturns') {
      const scenario = SCENARIO_OPTIONS.stockReturns[scenarioKey as keyof typeof SCENARIO_OPTIONS.stockReturns];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          stockReturnsScenario: scenarioKey,
          stockReturns: scenario.value
        }));
      }
    } else if (type === 'bondReturns') {
      const scenario = SCENARIO_OPTIONS.bondReturns[scenarioKey as keyof typeof SCENARIO_OPTIONS.bondReturns];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          bondReturnsScenario: scenarioKey,
          bondReturns: scenario.value
        }));
      }
    } else if (type === 'propertyTax') {
      const scenario = SCENARIO_OPTIONS.propertyTax[scenarioKey as keyof typeof SCENARIO_OPTIONS.propertyTax];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          propertyTaxScenario: scenarioKey,
          propertyTaxRate: scenario.value
        }));
      }
    } else if (type === 'maintenance') {
      const scenario = SCENARIO_OPTIONS.maintenanceRate[scenarioKey as keyof typeof SCENARIO_OPTIONS.maintenanceRate];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          maintenanceScenario: scenarioKey,
          maintenanceRate: scenario.value
        }));
      }
    } else if (type === 'vacancy') {
      const scenario = SCENARIO_OPTIONS.vacancyRate[scenarioKey as keyof typeof SCENARIO_OPTIONS.vacancyRate];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          vacancyScenario: scenarioKey,
          vacancyRate: scenario.value
        }));
      }
    } else if (type === 'management') {
      const scenario = SCENARIO_OPTIONS.managementFee[scenarioKey as keyof typeof SCENARIO_OPTIONS.managementFee];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          managementScenario: scenarioKey,
          managementFee: scenario.value
        }));
      }
    } else if (type === 'insuranceCost') {
      const scenario = SCENARIO_OPTIONS.insuranceCost[scenarioKey as keyof typeof SCENARIO_OPTIONS.insuranceCost];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          insuranceCostScenario: scenarioKey,
          insuranceCost: scenario.value
        }));
      }
    } else if (type === 'sellingCosts') {
      const scenario = SCENARIO_OPTIONS.sellingCosts[scenarioKey as keyof typeof SCENARIO_OPTIONS.sellingCosts];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          sellingCostsScenario: scenarioKey,
          sellingCosts: scenario.value
        }));
      }
    } else if (type === 'rentalSetupCosts') {
      const scenario = SCENARIO_OPTIONS.rentalSetupCosts[scenarioKey as keyof typeof SCENARIO_OPTIONS.rentalSetupCosts];
      if (scenario) {
        setAssumptions(prev => ({
          ...prev,
          rentalSetupCostsScenario: scenarioKey,
          rentalSetupCosts: scenario.value
        }));
      }
    }
  };

  const setPreset = (preset: PresetType) => {
    if (preset === 'rent') {
      // Favor rent scenario - good real estate performance vs average stocks
      setAssumptions(prev => ({
        ...prev,
        propertyAppreciationScenario: 'recent_strong',
        propertyAppreciation: 3.7,
        rentGrowthScenario: 'strong',
        rentGrowth: 2.0,
        stockReturnsScenario: 'historical_longterm',
        stockReturns: 7.5,
        bondReturnsScenario: 'current',
        bondReturns: 1.5,
        maintenanceScenario: 'well_maintained',
        maintenanceRate: 0.5,
        vacancyScenario: 'good',
        vacancyRate: 5.0,
        managementScenario: 'standard',
        managementFee: 8.0
      }));
    } else if (preset === 'sell') {
      // Favor sell scenario - good stock performance vs average real estate
      setAssumptions(prev => ({
        ...prev,
        propertyAppreciationScenario: 'historical_longterm',
        propertyAppreciation: 2.3,
        rentGrowthScenario: 'modest',
        rentGrowth: 1.0,
        stockReturnsScenario: 'historical_20yr',
        stockReturns: 8.5,
        bondReturnsScenario: 'rising_rates',
        bondReturns: 3.0,
        maintenanceScenario: 'well_maintained',
        maintenanceRate: 0.5,
        vacancyScenario: 'average',
        vacancyRate: 7.0,
        managementScenario: 'standard',
        managementFee: 8.0
      }));
    } else {
      // Even comparison - balanced historical averages
      setAssumptions(prev => ({
        ...prev,
        propertyAppreciationScenario: 'historical_longterm',
        propertyAppreciation: 2.3,
        rentGrowthScenario: 'modest',
        rentGrowth: 1.0,
        stockReturnsScenario: 'historical_longterm',
        stockReturns: 7.5,
        bondReturnsScenario: 'current',
        bondReturns: 1.5,
        maintenanceScenario: 'well_maintained',
        maintenanceRate: 0.5,
        vacancyScenario: 'good',
        vacancyRate: 5.0,
        managementScenario: 'standard',
        managementFee: 8.0
      }));
    }
  };

  const resetToDefaults = () => {
    // Clear localStorage
    localStorage.removeItem('enhanced-house-decision-assumptions');
    // Reset all assumptions to initial values
    setAssumptions(INITIAL_ASSUMPTIONS);
  };

  return {
    assumptions,
    updateAssumption,
    handleNumberInput,
    updateScenario,
    setPreset,
    resetToDefaults
  };
};